import React, { useState } from "react";
import { api, fmtInt } from "@/lib/api";
import { PageHeader, Section, Pill } from "@/components/ui-kit";
import { Upload, Download, CheckCircle2, AlertCircle, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const SLOTS = [
  {
    key: "books",
    title: "Catálogo de libros",
    description: "Importa o actualiza títulos por ISBN. Si el ISBN ya existe se actualiza.",
    template: "/api/import/templates/books.xlsx",
    endpoint: "/import/books",
    cols: ["isbn*", "title*", "subtitle", "authors", "illustrators", "pvp", "cost", "tax_rate", "pages", "age_range", "cover_url", "notes"],
  },
  {
    key: "customers",
    title: "Clientes",
    description: "Importa librerías, escuelas, ferias, particulares. Si coincide el NIF se actualiza.",
    template: "/api/import/templates/customers.xlsx",
    endpoint: "/import/customers",
    cols: ["name*", "type", "email", "phone", "nif", "address", "city", "country", "notes"],
  },
  {
    key: "stock",
    title: "Stock inicial",
    description: "Genera movimientos de tipo entrada_impresion. Idempotente: re-ejecutar no duplica unidades.",
    template: "/api/import/templates/stock.xlsx",
    endpoint: "/import/stock-initial",
    cols: ["isbn*", "warehouse_name*", "quantity*"],
  },
];

function downloadAuth(path, filename) {
  const url = `${process.env.REACT_APP_BACKEND_URL}${path}`;
  const token = localStorage.getItem("access_token");
  fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then(r => r.blob()).then(b => {
      const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = filename; a.click();
    });
}

function ImportSlot({ slot }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  async function onFile(f) {
    setFile(f); setPreview(null); setResult(null);
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo leer el archivo");
    } finally { setBusy(false); }
  }

  async function doImport() {
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(slot.endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("Importación completada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al importar");
    } finally { setBusy(false); }
  }

  return (
    <Section title={slot.title} description={slot.description} actions={
      <button onClick={() => downloadAuth(slot.template, `plantilla-${slot.key}.xlsx`)}
        data-testid={`tpl-${slot.key}`}
        className="inline-flex items-center gap-2 text-xs px-3 py-1.5 border border-line rounded-sm hover:bg-sidebar">
        <Download size={13} /> Plantilla .xlsx
      </button>
    }>
      <div className="p-5 space-y-4">
        <div className="text-xs text-ink2">
          <strong className="text-ink">Columnas esperadas:</strong>{" "}
          <span className="font-mono">{slot.cols.join(" · ")}</span>{" "}
          <span className="text-amber">(*) obligatorias</span>
        </div>

        <label className="flex items-center justify-center gap-3 border-2 border-dashed border-line rounded-sm py-8 px-4 cursor-pointer hover:border-brand hover:bg-sidebar/50 transition-colors"
          data-testid={`drop-${slot.key}`}>
          <Upload size={18} strokeWidth={1.5} className="text-ink2" />
          <span className="text-sm text-ink2">
            {file ? <span className="text-ink font-medium">{file.name}</span> : "Selecciona o arrastra un archivo .csv / .xlsx"}
          </span>
          <input type="file" accept=".csv,.xlsx,.xls" className="hidden"
            onChange={(e) => onFile(e.target.files[0])} data-testid={`input-${slot.key}`} />
        </label>

        {preview && (
          <div className="border border-line rounded-sm overflow-hidden">
            <div className="bg-sidebar/40 px-4 py-2 text-xs text-ink2 flex items-center justify-between">
              <span>Vista previa · {fmtInt(preview.total_rows)} filas detectadas (mostrando {preview.preview.length})</span>
              <button onClick={doImport} disabled={busy}
                data-testid={`import-${slot.key}`}
                className="bg-brand hover:bg-brand-hover text-white text-xs px-3 py-1.5 rounded-sm disabled:opacity-50">
                {busy ? "Importando…" : "Confirmar importación"}
              </button>
            </div>
            <div className="overflow-x-auto max-h-64">
              <table className="w-full text-xs">
                <thead><tr className="bg-sidebar/30 text-[10px] uppercase tracking-[0.1em] text-ink2">
                  {preview.columns.map(c => <th key={c} className="text-left px-3 py-2 font-medium">{c}</th>)}
                </tr></thead>
                <tbody>
                  {preview.preview.map((row, i) => (
                    <tr key={i} className="border-t border-line">
                      {preview.columns.map(c => <td key={c} className="px-3 py-1.5 font-mono">{String(row[c] ?? "")}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {result && (
          <div className="border border-line rounded-sm p-4 space-y-2" data-testid={`result-${slot.key}`}>
            <div className="flex items-center gap-3 text-sm flex-wrap">
              {(result.inserted ?? 0) > 0 && <Pill tone="success"><CheckCircle2 size={11} className="inline mr-1" />{result.inserted} insertados</Pill>}
              {(result.updated ?? 0) > 0 && <Pill tone="brand">{result.updated} actualizados</Pill>}
              {(result.skipped ?? 0) > 0 && <Pill tone="muted">{result.skipped} omitidos (duplicados)</Pill>}
              {(result.errors?.length ?? 0) > 0 && <Pill tone="danger"><AlertCircle size={11} className="inline mr-1" />{result.errors.length} errores</Pill>}
              <span className="text-xs text-ink2">{result.rows_processed ?? 0} filas procesadas</span>
            </div>
            {result.errors?.length > 0 && (
              <div className="border-t border-line pt-2 max-h-40 overflow-y-auto">
                <table className="w-full text-xs">
                  <tbody>
                    {result.errors.map((e, i) => (
                      <tr key={i} className="text-ink2">
                        <td className="px-2 py-0.5 font-mono w-16 text-crimson">Fila {e.row}</td>
                        <td className="px-2 py-0.5">{e.msg}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

export default function Importer() {
  return (
    <div data-testid="importer-page">
      <PageHeader
        title="Importar desde Excel / CSV"
        subtitle="Carga masiva del catálogo, clientes y stock inicial. Idempotente y con vista previa."
      />

      <div className="bg-amber/10 border border-amber/30 rounded-sm p-4 mb-6 flex gap-3 text-sm">
        <FileSpreadsheet size={18} strokeWidth={1.5} className="text-amber shrink-0 mt-0.5" />
        <div className="text-ink2">
          <strong className="text-ink">Orden recomendado:</strong> primero <span className="text-ink">Catálogo</span>, después <span className="text-ink">Clientes</span>, y por último <span className="text-ink">Stock inicial</span> (necesita que existan los libros y los almacenes en Configuración).
        </div>
      </div>

      <div className="space-y-6">
        {SLOTS.map(s => <ImportSlot key={s.key} slot={s} />)}
      </div>
    </div>
  );
}
